import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Standalone photo gallery: a slider over the images uploaded in the Media
// library. Renders nothing until there is at least one image.
const PhotoGallery = () => {
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const q = query(collection(db, 'media'), where('type', '==', 'image'));
        const snap = await getDocs(q);
        const all = snap.docs.map((d) => d.data() as { url: string; homepageOrder?: number });

        // Only the images picked in the Media library, in the chosen order.
        // When nothing is picked yet, fall back to showing everything so an
        // existing site does not lose its gallery.
        const picked = all
          .filter((m) => typeof m.homepageOrder === 'number')
          .sort((a, b) => (a.homepageOrder as number) - (b.homepageOrder as number));

        setImages((picked.length > 0 ? picked : all).map((m) => m.url));
      } catch (error) {
        console.error('Error fetching gallery images:', error);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  if (loading || images.length === 0) return null;

  const prev = () => setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setIndex((i) => (i + 1) % images.length);

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container-custom">
        <div className="text-center mb-10">
          <h2 className="section-title">Галерия</h2>
        </div>

        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
          <div className="relative group">
            <img
              src={images[index]}
              alt={`Галерия ${index + 1}`}
              className="w-full h-[420px] md:h-[600px] object-cover transition-opacity duration-500"
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
                  aria-label="Предишна снимка"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
                  aria-label="Следваща снимка"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIndex(i)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        i === index ? 'bg-white scale-110' : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Отиди на снимка ${i + 1}`}
                    />
                  ))}
                </div>

                <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {index + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhotoGallery;
